import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatINR, discountPct } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import ProductCard from "@/components/product/ProductCard";
import { Star, MessageCircle, ShoppingBag, Heart, Truck, ShieldCheck, Sparkles, Minus, Plus, ChevronLeft, ChevronRight } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem } from "@/components/ui/carousel";
import { toast } from "sonner";

export default function ProductDetail() {
  const { slug } = useParams();
  const { addToCart, toggleWishlist, inWishlist, pushRecent, settings, recent } = useApp();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [tab, setTab] = useState("overview");
  const [related, setRelated] = useState([]);
  const [notifyEmail, setNotifyEmail] = useState("");
  const [notifying, setNotifying] = useState(false);
  const [carouselApi, setCarouselApi] = useState(null);
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setProduct(null);
    api.get(`/products/${slug}`).then((r) => {
      if (cancelled) return;
      setProduct(r.data);
      pushRecent(r.data);
      api.get("/products", { params: { category: r.data.category, limit: 8 } }).then((r2) => {
        if (cancelled) return;
        setRelated((r2.data.items || []).filter((p) => p.id !== r.data.id).slice(0, 4));
      });
    });
    return () => { cancelled = true; };
  }, [slug, pushRecent]);

  useEffect(() => {
    if (!carouselApi) return;
    const syncActiveImage = () =>
      setActiveImageIndex(carouselApi.selectedScrollSnap());
    carouselApi.on("select", syncActiveImage);
    syncActiveImage();
    return () => carouselApi.off("select", syncActiveImage);
  }, [carouselApi]);

  if (!product) return <div className="max-w-[1400px] mx-auto p-8 text-white/50">Loading...</div>;

  const disc = discountPct(product.price, product.mrp);
  const oos = (product.stock || 0) <= 0;
  const wl = inWishlist(product.id);

  const productImages = product.images?.length ? product.images : [""];

  const notifyMe = async (e) => {
    e.preventDefault();
    setNotifying(true);
    try {
      await api.post("/notify-me", { product_id: product.id, email: notifyEmail });
      toast.success("We'll notify you when this is back in stock.");
      setNotifyEmail("");
    } catch { toast.error("Something went wrong."); }
    setNotifying(false);
  };

  const s = settings || {};

  return (
    <div data-testid="product-page" className="pb-safe-buy">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 pt-6 md:pt-10">
        <div className="overline text-white/50 mb-4">
          <Link to="/" className="hover:text-white">Home</Link> / <Link to="/shop" className="hover:text-white">Shop</Link> / <span className="text-white">{product.category}</span>
        </div>

        <div className="grid md:grid-cols-2 gap-6 lg:gap-14">
          {/* Gallery */}
          <div className="space-y-3">
            <Carousel setApi={setCarouselApi} opts={{ loop: true }} aria-label={`${product.name} images`}>
              <CarouselContent className="ml-0">
                {productImages.map((img, i) => (
                  <CarouselItem key={img || i} className="pl-0">
                    <div className="aspect-square bg-black border border-white/10 overflow-hidden">
                      {img ? <img src={img} alt={`${product.name}, image ${i + 1}`} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-white/20 font-display">No image</div>}
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              {productImages.length > 1 && (
                <>
                  <button type="button" onClick={() => carouselApi?.scrollPrev()} aria-label="Previous image" data-testid="product-image-previous" className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-black/75 border border-white/20 text-white hover:bg-black">
                    <ChevronLeft size={20} />
                  </button>
                  <button type="button" onClick={() => carouselApi?.scrollNext()} aria-label="Next image" data-testid="product-image-next" className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-black/75 border border-white/20 text-white hover:bg-black">
                    <ChevronRight size={20} />
                  </button>
                  <span className="absolute bottom-3 right-3 px-2 py-1 bg-black/75 text-xs text-white" aria-live="polite">
                    {activeImageIndex + 1} / {productImages.length}
                  </span>
                </>
              )}
            </Carousel>
            {productImages.length > 1 && (
              <div className="grid grid-cols-4 gap-2">
                {productImages.slice(0, 8).map((img, i) => (
                  <button type="button" key={img || i} onClick={() => carouselApi?.scrollTo(i)} aria-label={`Show image ${i + 1}`} aria-pressed={activeImageIndex === i} data-testid={`product-image-thumbnail-${i}`} className={`aspect-square bg-black border overflow-hidden ${activeImageIndex === i ? "border-[#E63946]" : "border-white/10"}`}>
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div>
            <div className="overline text-[#E63946]">{product.category}{product.subcategory ? ` · ${product.subcategory}` : ""}</div>
            <h1 className="font-display text-3xl md:text-5xl tracking-tighter mt-3 leading-[1.05]" data-testid="product-title">{product.name}</h1>

            <div className="flex items-center gap-4 mt-4">
              <div className="flex items-center gap-1">
                {[0,1,2,3,4].map((i) => <Star key={i} size={14} fill={i < Math.round(product.rating || 4) ? "#E63946" : "transparent"} stroke="#E63946" />)}
              </div>
              <span className="text-xs text-white/50">{product.review_count || 0} reviews</span>
            </div>

            <div className="flex items-baseline gap-3 mt-6">
              <div className="font-display text-4xl md:text-5xl text-white" data-testid="product-price">{formatINR(product.price)}</div>
              {product.mrp > product.price && (
                <>
                  <div className="text-white/50 line-through">{formatINR(product.mrp)}</div>
                  <div className="overline text-[#E63946]">-{disc}% OFF</div>
                </>
              )}
            </div>
            <div className="mt-2 text-xs text-white/50">Inclusive of all taxes</div>

            <div className="mt-4">
              {oos ? (
                <span className="overline text-white/70 border border-white/15 px-3 py-1 inline-block">Currently Unavailable</span>
              ) : (
                <span className="overline text-emerald-400 border border-emerald-400/30 px-3 py-1 inline-block">In Stock</span>
              )}
            </div>

            {!oos ? (
              <div className="mt-8 space-y-4">
                <div className="flex items-center gap-4">
                  <div className="inline-flex items-center border border-white/15">
                    <button className="p-3" onClick={() => setQty((q) => Math.max(1, q - 1))} data-testid="qty-dec"><Minus size={16} /></button>
                    <span className="px-5 min-w-[3rem] text-center">{qty}</span>
                    <button className="p-3" onClick={() => setQty((q) => q + 1)} data-testid="qty-inc"><Plus size={16} /></button>
                  </div>
                  <button onClick={() => toggleWishlist(product)} className={`w-12 h-12 border ${wl ? "border-[#E63946] text-[#E63946]" : "border-white/15"} flex items-center justify-center`} data-testid="pdp-wishlist">
                    <Heart size={18} fill={wl ? "currentColor" : "none"} />
                  </button>
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <button onClick={() => addToCart(product, qty)} className="sa-btn sa-btn-primary flex-1 py-4" data-testid="add-to-cart">
                    <ShoppingBag size={16} /> ADD TO CART
                  </button>
                  <Link to="/checkout" onClick={() => addToCart(product, qty)} className="sa-btn sa-btn-white flex-1 py-4" data-testid="buy-now">
                    BUY NOW
                  </Link>
                </div>
              </div>
            ) : (
              <form onSubmit={notifyMe} className="mt-8 space-y-3" data-testid="notify-me-form">
                <div className="overline text-white/60">Get notified when available</div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <input required type="email" value={notifyEmail} onChange={(e) => setNotifyEmail(e.target.value)} placeholder="Your email" className="sa-input" />
                  <button disabled={notifying} className="sa-btn sa-btn-primary">NOTIFY ME</button>
                </div>
              </form>
            )}

            {/* Support */}
            <div className="mt-8 grid grid-cols-2 gap-3">
              <Link to="/ask-expert" className="sa-btn sa-btn-outline text-xs">ASK AN EXPERT</Link>
              <a href={`https://wa.me/91${s.whatsapp || ""}`} target="_blank" rel="noreferrer" className="sa-btn sa-btn-outline text-xs">
                <MessageCircle size={14} /> WHATSAPP
              </a>
            </div>

            {/* Trust bar */}
            <div className="mt-8 grid grid-cols-3 gap-2 text-[11px] text-white/60">
              <div className="border border-white/10 p-3 flex flex-col gap-2"><Truck size={16} className="text-[#E63946]" />Pan-India Delivery</div>
              <div className="border border-white/10 p-3 flex flex-col gap-2"><ShieldCheck size={16} className="text-[#E63946]" />Secure Payment</div>
              <div className="border border-white/10 p-3 flex flex-col gap-2"><Sparkles size={16} className="text-[#E63946]" />{s.shipping_estimate_days || "3-7 days"}</div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-16 border-t border-white/10 pt-10">
          <div className="flex flex-wrap gap-2 md:gap-6 mb-6">
            {[
              { k: "overview", l: "Overview" },
              { k: "features", l: "Features" },
              { k: "specs", l: "Specifications" },
              { k: "included", l: "What's Included" },
              { k: "info", l: "Important Info" },
            ].map((t) => (
              <button key={t.k} onClick={() => setTab(t.k)} className={`overline pb-2 border-b-2 ${tab === t.k ? "border-[#E63946] text-white" : "border-transparent text-white/50 hover:text-white/80"}`} data-testid={`tab-${t.k}`}>{t.l}</button>
            ))}
          </div>

          <div className="max-w-3xl text-white/80 leading-relaxed text-sm md:text-base">
            {tab === "overview" && <p className="whitespace-pre-line">{product.description || "A carefully selected accessory to elevate your drive."}</p>}
            {tab === "features" && (
              <ul className="space-y-2">
                {(product.features?.length ? product.features : ["Premium materials", "Easy installation", "Universal fitment"]).map((f, i) => (
                  <li key={`${f}-${i}`} className="flex items-start gap-3"><span className="w-1.5 h-1.5 bg-[#E63946] mt-2 flex-shrink-0"></span>{f}</li>
                ))}
              </ul>
            )}
            {tab === "specs" && (
              <div className="border border-white/10">
                {Object.entries(product.specs || {}).length ? Object.entries(product.specs).map(([k, v]) => (
                  <div key={k} className="grid grid-cols-2 border-b border-white/5 last:border-b-0">
                    <div className="p-3 overline text-white/60 bg-white/5">{k}</div>
                    <div className="p-3 text-sm">{v}</div>
                  </div>
                )) : <div className="p-4 text-white/50 text-sm">Specifications will be listed here.</div>}
              </div>
            )}
            {tab === "included" && (
              <ul className="space-y-2">
                {(product.whats_included?.length ? product.whats_included : ["1 x " + product.name]).map((f, i) => <li key={`${f}-${i}`}>• {f}</li>)}
              </ul>
            )}
            {tab === "info" && <p className="text-white/70 whitespace-pre-line">{product.important_info || "Please check compatibility with your car before purchasing. For assistance, use 'Ask an Expert'."}</p>}
          </div>
        </div>

        {/* Related */}
        {related.length > 0 && (
          <div className="mt-16">
            <div className="overline text-[#E63946] mb-3">You May Also Like</div>
            <h2 className="font-display text-3xl md:text-4xl tracking-tighter mb-6">COMPLETE THE LOOK</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {related.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          </div>
        )}

        {/* Recently viewed */}
        {recent.length > 1 && (
          <div className="mt-16">
            <div className="overline text-white/50 mb-3">Recently Viewed</div>
            <h3 className="font-display text-2xl md:text-3xl tracking-tighter mb-6">CONTINUE BROWSING</h3>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
              {recent.filter((r) => r.id !== product.id).slice(0, 6).map((r) => (
                <Link key={r.id} to={`/product/${r.slug}`} className="block group" data-testid={`recent-${r.slug}`}>
                  <div className="aspect-square bg-white/5 border border-white/10 overflow-hidden">
                    {r.image && <img src={r.image} alt={r.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
                  </div>
                  <div className="mt-2 text-xs line-clamp-2 min-h-[2rem]">{r.name}</div>
                  <div className="text-[#E63946] font-bold text-sm">{formatINR(r.price)}</div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Sticky mobile buy bar */}
      {!oos && (
        <div className="md:hidden fixed bottom-[68px] inset-x-0 z-30 bg-[#0A0A0A]/95 backdrop-blur-xl border-t border-white/10 p-3 flex items-center gap-3" data-testid="mobile-buy-bar">
          <div>
            <div className="text-[10px] text-white/50 uppercase tracking-widest">Price</div>
            <div className="font-display text-lg">{formatINR(product.price * qty)}</div>
          </div>
          <button onClick={() => addToCart(product, qty)} className="sa-btn sa-btn-primary flex-1 py-3 text-xs" data-testid="mobile-add-to-cart">ADD TO CART</button>
        </div>
      )}
    </div>
  );
}
